import React, { useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import InfiniteScroll, {
  Props as InfiniteScrollProps,
} from 'react-infinite-scroll-component';
import { IconChevronDown } from '../../../../assets/svg/icons';
import useToggle from '../../../../hooks/useToggle';
import {
  StyledMultiSelectButton,
  StyledMultiSelectButtonDecor,
  StyledMultiSelectButtonIcon,
  StyledMultiSelectButtonText,
  StyledMultiSelectContent,
  StyledMultiSelectEntityBadge,
  StyledMultiSelectWrapper,
} from './styles';
import { MultiSelectProps, MultiSelectValue } from './types';
import RadioList from '../Radio';
import renderRadioItemWithCheckboxes from '../Radio/content/withCheckboxes';
import { RadioItemWithCheckboxesProps } from '../Radio/content/withCheckboxes/types';
import { StyledBox } from '../../../styles/Grid/styles';

const MultiSelect: React.FC<MultiSelectProps> = ({
  placeholder,
  icon: Icon,
  items,
  disabled,
  value,
  infiniteScrollProps,
  onChange,
  renderSelectedItems,
  renderTrigger,
}) => {
  const [isOpen, { toggle, toggleOff, registerContainerRef }] = useToggle();

  const { scrollableTarget } = infiniteScrollProps ?? {};

  const RadioListParent = infiniteScrollProps ? InfiniteScroll : React.Fragment;

  const filteredItems = useMemo(() => {
    if (!renderSelectedItems) {
      return items;
    }

    return items.filter(({ value: itemValue }) => itemValue !== value?.value);
  }, [items, renderSelectedItems, value?.value]);

  const selectedItem = useMemo(
    () => items.find((item) => item.value === value?.value),
    [items, value?.value]
  );

  const entitySummary = useMemo(() => {
    if (!selectedItem || !value) return null;
    const total = selectedItem.checkboxes?.length ?? 0;
    const checked = value.checkedSubItems?.length ?? 0;
    if (total === 0 || checked === 0) return null;
    return checked === total ? null : `${checked}/${total}`;
  }, [selectedItem, value]);

  return (
    <StyledMultiSelectWrapper ref={registerContainerRef}>
      {renderTrigger ? (
        renderTrigger({ toggle, isOpen, entitySummary })
      ) : (
        <StyledMultiSelectButton type='button' onClick={toggle} {...{ disabled }}>
          {Icon && <StyledMultiSelectButtonIcon><Icon /></StyledMultiSelectButtonIcon>}
          <StyledMultiSelectButtonText>
            {selectedItem?.label || placeholder}
          </StyledMultiSelectButtonText>
          {entitySummary && (
            <StyledMultiSelectEntityBadge>{entitySummary}</StyledMultiSelectEntityBadge>
          )}
          <StyledMultiSelectButtonDecor>
            <IconChevronDown />
          </StyledMultiSelectButtonDecor>
        </StyledMultiSelectButton>
      )}
      <AnimatePresence>
        {isOpen && (
          <StyledMultiSelectContent
            id={
              typeof scrollableTarget === 'string'
                ? scrollableTarget
                : undefined
            }
          >
            <RadioListParent
              {...((infiniteScrollProps as InfiniteScrollProps) ?? {})}
            >
              {renderSelectedItems && (
                <StyledBox mb='1rem'>{renderSelectedItems(value)}</StyledBox>
              )}

              <RadioList<string, RadioItemWithCheckboxesProps, MultiSelectValue>
                {...{ value }}
                items={filteredItems}
                allowUnselect
                onChange={(selectedValue) => {
                  // allowUnselect turns the radio into a checkbox — when the
                  // already-selected client is clicked it returns undefined.
                  // Treat that as "re-select this client with all entities".
                  const resolvedValue = selectedValue ?? value?.value;
                  if (!resolvedValue) return;

                  if (value?.value !== resolvedValue) {
                    toggleOff();
                  }

                  const selectedItem = items.find(
                    (item) => item.value === resolvedValue
                  );

                  if (!selectedItem || !onChange) {
                    return;
                  }

                  onChange(selectedItem);
                }}
                renderRadioContent={renderRadioItemWithCheckboxes}
                isValueCheckedCallback={(valueToCompare, selectedValue) => {
                  const selectedItem = items.find(
                    (item) => item.value === selectedValue?.value
                  );

                  return (
                    selectedValue?.value === valueToCompare &&
                    !!selectedItem &&
                    selectedItem.checkboxes.every((checkbox) =>
                      selectedValue.checkedSubItems.includes(checkbox.value)
                    )
                  );
                }}
              />
            </RadioListParent>
          </StyledMultiSelectContent>
        )}
      </AnimatePresence>
    </StyledMultiSelectWrapper>
  );
};

export default MultiSelect;
